import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';

import { cn } from '#/lib/utils';

import { canPrintStockReceiptLabel } from '#/modules/imsMgmt/receipts/shared/receiptStatus';

import StockReceiptLinesTable from './StockReceiptLinesTable';
import StockReceiptPrintLabelButton from './StockReceiptPrintLabelButton';

const StockReceiptDetailSection = ({
  detail,
  defaultOpen = true,
  embedded = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [selectedInventoryStockIds, setSelectedInventoryStockIds] = useState(() => new Set());
  const canPrintLabel = canPrintStockReceiptLabel(detail?.status);
  const receiptNo = detail?.receiptNo || '-';
  const lines = detail?.lines ?? [];

  const handleToggleParent = useCallback((childIds, checked) => {
    setSelectedInventoryStockIds((prev) => {
      const next = new Set(prev);
      childIds.forEach((id) => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  }, []);

  const handleToggleChild = useCallback((inventoryStockId, checked) => {
    setSelectedInventoryStockIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(inventoryStockId);
      } else {
        next.delete(inventoryStockId);
      }
      return next;
    });
  }, []);

  const selectedIdsArray = useMemo(
    () => Array.from(selectedInventoryStockIds),
    [selectedInventoryStockIds],
  );

  const linesTable = (
    <StockReceiptLinesTable
      lines={lines}
      canPrintLabel={canPrintLabel}
      selectedInventoryStockIds={selectedInventoryStockIds}
      onToggleParent={handleToggleParent}
      onToggleChild={handleToggleChild}
    />
  );

  const sectionHeader = (
    <div
      className={cn(
        'flex items-center justify-between gap-3 px-4 py-3',
        embedded && 'border-t border-slate-200',
        open && 'bg-slate-50',
        embedded && !open && 'rounded-b-2xl',
        !embedded && (open ? 'rounded-t-2xl' : 'rounded-2xl'),
      )}
    >
      <button
        type="button"
        className="
          flex min-w-0 flex-1 items-center gap-3 text-left transition-colors
          hover:text-primary-600
        "
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <ChevronDown
          size={18}
          className={cn(
            'shortcut_transition-transform shrink-0 origin-center text-gray-600',
            open ? 'rotate-0' : '-rotate-90',
          )}
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-gray-800">入庫明細</h2>
          <p className="truncate text-sm text-gray-500">{receiptNo}</p>
        </div>
      </button>
      {canPrintLabel ? (
        <div
          className="shrink-0"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          role="presentation"
        >
          <StockReceiptPrintLabelButton
            lines={lines}
            selectedInventoryStockIds={selectedIdsArray}
            defaultItemId="all"
          />
        </div>
      ) : null}
    </div>
  );

  const sectionBody = (
    <div
      className={cn(
        'grid shortcut_transition-disclosure',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
      aria-hidden={!open}
    >
      <div className="min-h-0 overflow-hidden rounded-b-2xl">
        <div className="stock-receipt-detail-section__scroll">
          {linesTable}
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <>
        {sectionHeader}
        {sectionBody}
      </>
    );
  }

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface">
        {sectionHeader}
        {sectionBody}
      </div>
    </div>
  );
};

StockReceiptDetailSection.propTypes = {
  detail: PropTypes.shape({
    receiptNo: PropTypes.string,
    status: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  defaultOpen: PropTypes.bool,
  embedded: PropTypes.bool,
};

export default StockReceiptDetailSection;
